import { useMemo, useState } from "react";
import { C } from "../lib/theme";
import { Card } from "../components/Card";
import { Btn } from "../components/Btn";
import { Eyebrow } from "../components/Eyebrow";
import { Icon } from "../components/Icon";
import { Skeleton } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { useStores } from "../hooks/useStores";
import { gradientForId } from "../lib/gradients";

const SORTS = [
  ["popular", "Popular"],
  ["new", "New"],
  ["rating", "Top rated"],
];

export function Stores({ onVisit }) {
  const { stores, loading } = useStores({ limit: 60 });
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("popular");

  const sorted = useMemo(() => {
    const arr = [...stores];
    if (sort === "new") arr.reverse();
    else if (sort === "rating") arr.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
    else arr.sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0));
    return arr;
  }, [stores, sort]);

  const filtered = useMemo(() => {
    if (!query.trim()) return sorted;
    const q = query.toLowerCase();
    return sorted.filter(
      (s) =>
        s.store_name?.toLowerCase().includes(q) ||
        s.category?.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
    );
  }, [sorted, query]);

  const featured = sorted.slice(0, 4);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 18 }}>
        <div>
          <Eyebrow>Stores · {stores.length} on Kapio</Eyebrow>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0", letterSpacing: "-0.6px" }}>
            Browse all stores
          </h1>
        </div>
        <Btn kind="outline" size="sm" icon="map">
          View on map
        </Btn>
      </div>

      <Card pad={14} style={{ marginBottom: 18 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              borderRadius: 12,
              background: C.cream,
            }}
          >
            <Icon name="search" size={15} color={C.muted} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search stores by name, category, or product…"
              style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13, fontFamily: "inherit" }}
            />
          </div>
          <div style={{ display: "flex", gap: 4, padding: 3, borderRadius: 10, background: C.cream }}>
            {SORTS.map(([k, l]) => {
              const on = sort === k;
              return (
                <button
                  key={k}
                  onClick={() => setSort(k)}
                  style={{
                    border: "none",
                    background: on ? C.white : "transparent",
                    color: on ? C.hunter : C.muted,
                    padding: "6px 12px",
                    borderRadius: 7,
                    fontSize: 11.5,
                    fontWeight: 800,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    boxShadow: on ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {loading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} h={220} r={16} />
          ))}
        </div>
      ) : featured.length > 0 ? (
        <>
          <div style={{ marginBottom: 26 }}>
            <h3 style={{ fontSize: 15, fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.3px" }}>
              Featured this week
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14 }}>
              {featured.map((s) => (
                <FeaturedStoreCard key={s.id} store={s} onVisit={onVisit} />
              ))}
            </div>
          </div>

          <h3 style={{ fontSize: 15, fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.3px" }}>
            All stores · {filtered.length}
          </h3>
          {filtered.length === 0 ? (
            <EmptyState icon="store" title="No matches" body="Try a different search term." />
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
              {filtered.map((s) => (
                <StoreRow key={s.id} store={s} onVisit={onVisit} />
              ))}
            </div>
          )}
        </>
      ) : (
        <EmptyState icon="store" title="No stores yet" body="Sellers are being onboarded — check back soon." />
      )}
    </div>
  );
}

function FeaturedStoreCard({ store, onVisit }) {
  const [g1, g2] = gradientForId(store.id);
  return (
    <button onClick={() => onVisit(store)} style={{ all: "unset", cursor: "pointer", display: "block" }}>
      <Card pad={0} style={{ height: "100%" }}>
        <div
          style={{
            height: 140,
            background: store.cover_url ? `url(${store.cover_url}) center/cover` : `linear-gradient(135deg, ${g1}, ${g2})`,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              padding: "3px 8px",
              borderRadius: 6,
              background: "rgba(255,255,255,0.95)",
              fontSize: 9.5,
              fontWeight: 800,
              color: C.hunter,
              letterSpacing: 0.5,
            }}
          >
            FEATURED
          </div>
        </div>
        <div style={{ padding: "0 14px 14px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: store.logo_url
                  ? `url(${store.logo_url}) center/cover ${C.white}`
                  : `linear-gradient(135deg, ${g1}, ${g2})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                fontWeight: 900,
                color: C.white,
                border: "3px solid " + C.white,
                marginTop: -28,
                boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
                flexShrink: 0,
                position: "relative",
                zIndex: 2,
              }}
            >
              {!store.logo_url && store.store_name?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingTop: 10 }}>
              <div style={{ fontSize: 13.5, fontWeight: 900 }}>{store.store_name}</div>
              <div style={{ fontSize: 10.5, color: C.muted }}>{store.category}</div>
            </div>
          </div>
          {store.description && (
            <p
              style={{
                fontSize: 11.5,
                color: C.slate,
                margin: "4px 0 10px",
                lineHeight: 1.4,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {store.description}
            </p>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 700 }}>
            <span>⭐ {(store.rating_avg || 0).toFixed(1)}</span>
            <span>{store.product_count || 0} items</span>
            <span>{store.total_orders || 0} orders</span>
          </div>
        </div>
      </Card>
    </button>
  );
}

function StoreRow({ store, onVisit }) {
  const [g1, g2] = gradientForId(store.id);
  return (
    <button onClick={() => onVisit(store)} style={{ all: "unset", cursor: "pointer", display: "block" }}>
      <Card pad={14}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <div
            style={{
              width: 70,
              height: 70,
              borderRadius: 14,
              background: store.logo_url
                ? `url(${store.logo_url}) center/cover`
                : `linear-gradient(135deg, ${g1}, ${g2})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
              fontWeight: 900,
              color: C.white,
              flexShrink: 0,
            }}
          >
            {!store.logo_url && store.store_name?.[0]?.toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 900 }}>{store.store_name}</div>
            <div
              style={{
                fontSize: 11.5,
                color: C.muted,
                margin: "3px 0 6px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {store.description || store.category}
            </div>
            <div style={{ display: "flex", gap: 12, fontSize: 11, fontWeight: 700, color: C.slate }}>
              <span>⭐ {(store.rating_avg || 0).toFixed(1)}</span>
              <span>·</span>
              <span>{store.product_count || 0} items</span>
              {store.category && (
                <>
                  <span>·</span>
                  <span style={{ color: C.hunter }}>{store.category}</span>
                </>
              )}
            </div>
          </div>
          <Icon name="chevron-right" size={18} color={C.muted} />
        </div>
      </Card>
    </button>
  );
}
